import { useEffect, useRef, useState } from 'react';
import type { Audience, MailAccountView } from '@epasu/core';
import { crm, type CampaignRow, type LeadListRow } from '@/lib/crm-client';
import { Button } from '@/components/ui/button';
import { Input, Select } from '@/components/ui/input';
import { versanddauer, WOCHENTAGE } from '@/lib/campaign-schedule';
import { cn } from '@/lib/utils';

interface Props {
  audiences: Audience[];
  /**
   * Gesetzt heisst bearbeiten statt anlegen. Beide Wege fragen dasselbe ab,
   * deshalb ein Dialog: zwei Formulare mit denselben fuenf Feldern waeren
   * zwei Orte, an denen dieselbe Regel gepflegt werden muesste.
   */
  kampagne?: CampaignRow;
  onClose: () => void;
  onSaved: (campaignId: string) => void;
}

/**
 * Eine Kampagne anlegen oder aendern.
 *
 * Die Zielgruppe steht mit der Anlage fest — die Mitglieder haengen daran, und
 * eine laufende Kampagne springt nicht zwischen Zielgruppen. Die Quell-Liste
 * dagegen laesst sich wechseln: sie ist Herkunft, keine laufende Verbindung,
 * und ein Wechsel ruehrt nichts an, was schon aufgenommen oder versendet
 * wurde. Nachgezogen wird ohnehin nur auf ausdruecklichen Klick im
 * Mitglieder-Reiter.
 */
export function CampaignDialog({ audiences, kampagne, onClose, onSaved }: Props) {
  const bearbeiten = kampagne !== undefined;

  const [name, setName] = useState(kampagne?.name ?? '');
  const [audienceId, setAudienceId] = useState(kampagne?.audienceId ?? '');
  const [sourceListId, setSourceListId] = useState(kampagne?.sourceListId ?? '');
  const [senderAccountId, setSenderAccountId] = useState(kampagne?.senderAccountId ?? '');
  const [dailyLimit, setDailyLimit] = useState(String(kampagne?.dailyLimit ?? 50));
  const [sendAtTime, setSendAtTime] = useState(kampagne?.sendAtTime ?? '');
  const [sendStartDate, setSendStartDate] = useState(kampagne?.sendStartDate ?? '');
  const [sendWeekdays, setSendWeekdays] = useState(kampagne?.sendWeekdays ?? '12345');
  const [trackOpens, setTrackOpens] = useState(kampagne?.trackOpens ?? false);
  const [pauseMin, setPauseMin] = useState(String(kampagne?.sendPauseMinSec ?? 10));
  const [pauseMax, setPauseMax] = useState(String(kampagne?.sendPauseMaxSec ?? 30));

  const [listen, setListen] = useState<LeadListRow[]>([]);
  const [konten, setKonten] = useState<MailAccountView[]>([]);

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const nameRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    nameRef.current?.focus();
    // Auch die archivierten: eine Kampagne kann eine Liste als Herkunft
    // fuehren, die seither archiviert wurde — die soll der Dialog anzeigen
    // koennen, statt sie beim Oeffnen stillschweigend zu verlieren.
    void crm.leadLists.list({ includeArchived: true }).then(setListen);
    void crm.mailAccounts.list().then((alle) => setKonten(alle.filter((k) => k.active)));
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !busy) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [busy, onClose]);

  // Nur Listen derselben Zielgruppe ergeben als Herkunft Sinn — `addFromSourceList`
  // prüft die Zielgruppe ohnehin bei jeder Aufnahme, aber eine Liste, die von
  // vornherein nicht passt, sollte gar nicht erst zur Wahl stehen.
  const passendeListen = listen.filter((l) => !l.archived && l.audienceId === audienceId);

  // Eine archivierte Liste steht nicht mehr zur Wahl — die bereits eingetragene
  // aber schon: sonst schriebe das blosse Öffnen des Dialogs die Herkunft um.
  const eingetragene =
    sourceListId && !passendeListen.some((l) => l.id === sourceListId)
      ? listen.find((l) => l.id === sourceListId)
      : undefined;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !audienceId) {
      setError('Name und Zielgruppe sind Pflicht.');
      return;
    }
    const limit = Number(dailyLimit);
    if (!Number.isFinite(limit) || limit <= 0) {
      setError('Das Tageslimit muss eine Zahl größer null sein.');
      return;
    }
    // Ein Zeitplan ohne Sendetag liefe nie — das ist kein Zeitplan, sondern
    // eine Kampagne, die stillschweigend nichts tut.
    if (sendAtTime && sendWeekdays === '') {
      setError('Wähle mindestens einen Sendetag.');
      return;
    }
    const min = Number(pauseMin);
    const max = Number(pauseMax);
    if (!Number.isInteger(min) || !Number.isInteger(max) || min < 0 || max < 0) {
      setError('Die Pause braucht ganze Sekunden ab 0.');
      return;
    }
    if (min > max) {
      setError('Bei der Pause darf „von“ nicht größer sein als „bis“.');
      return;
    }
    const zeitplan = {
      sendAtTime: sendAtTime || null,
      // Ohne Uhrzeit hat weder Startdatum noch Sendetag eine Bedeutung.
      sendStartDate: sendAtTime ? sendStartDate || null : null,
      sendWeekdays: sendAtTime ? sendWeekdays : '12345',
      sendPauseMinSec: min,
      sendPauseMaxSec: max,
    };
    setBusy(true);
    setError(null);
    try {
      if (kampagne) {
        await crm.campaigns.update(kampagne.id, {
          name: name.trim(),
          sourceListId: sourceListId || null,
          senderAccountId: senderAccountId || null,
          dailyLimit: limit,
          trackOpens,
          ...zeitplan,
        });
        onSaved(kampagne.id);
      } else {
        const angelegt = await crm.campaigns.create({
          name: name.trim(),
          audienceId,
          sourceListId: sourceListId || null,
          senderAccountId: senderAccountId || null,
          dailyLimit: limit,
          trackOpens,
          ...zeitplan,
        });
        onSaved(angelegt.id);
      }
    } catch (err) {
      setError(
        err instanceof Error ? err.message : bearbeiten ? 'Speichern fehlgeschlagen' : 'Anlegen fehlgeschlagen',
      );
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex justify-center overflow-y-auto bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      aria-label={bearbeiten ? 'Kampagne bearbeiten' : 'Neue Kampagne'}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !busy) onClose();
      }}
    >
      <form
        onSubmit={(e) => void submit(e)}
        className="my-auto w-full max-w-md rounded-lg border bg-background p-6 shadow-lg"
      >
        <h2 className="text-lg font-semibold">
          {bearbeiten ? 'Kampagne bearbeiten' : 'Neue Kampagne'}
        </h2>

        <div className="mt-4 space-y-4">
          <Field label="Name">
            <Input
              ref={nameRef}
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Grundschulen Nord, Herbst 2026"
            />
          </Field>

          <Field label={bearbeiten ? 'Zielgruppe (steht fest)' : 'Zielgruppe'}>
            <Select
              value={audienceId}
              disabled={bearbeiten}
              onChange={(e) => {
                setAudienceId(e.target.value);
                setSourceListId('');
              }}
            >
              <option value="">— wählen —</option>
              {audiences.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name}
                </option>
              ))}
            </Select>
          </Field>

          <Field label="Quell-Liste (optional)">
            <Select
              value={sourceListId}
              onChange={(e) => setSourceListId(e.target.value)}
              disabled={!audienceId}
            >
              <option value="">— keine —</option>
              {passendeListen.map((l) => (
                <option key={l.id} value={l.id}>
                  {l.name} ({l.memberCount})
                </option>
              ))}
              {eingetragene && (
                <option value={eingetragene.id}>{eingetragene.name} (archiviert)</option>
              )}
            </Select>
            {bearbeiten && (
              <span className="text-xs text-muted-foreground">
                Ein Wechsel ändert nur, woher künftig nachgezogen wird — aufgenommene
                Mitglieder bleiben.
              </span>
            )}
          </Field>

          <Field label="Absenderpostfach">
            <Select value={senderAccountId} onChange={(e) => setSenderAccountId(e.target.value)}>
              <option value="">Standardkonto</option>
              {konten.map((k) => (
                <option key={k.id} value={k.id}>
                  {k.label} — {k.fromAddress}
                </option>
              ))}
            </Select>
          </Field>

          <Field label="Tageslimit">
            <Input
              inputMode="numeric"
              value={dailyLimit}
              onChange={(e) => setDailyLimit(e.target.value)}
            />
          </Field>

          <label className="flex items-start gap-2 text-sm">
            <input
              type="checkbox"
              className="mt-0.5 h-4 w-4"
              checked={trackOpens}
              onChange={(e) => setTrackOpens(e.target.checked)}
            />
            <span>
              Öffnungen messen
              <span className="block text-xs text-muted-foreground">
                Setzt einen unsichtbaren Zählpixel in jede Mail dieser Kampagne. Bei kalter
                Ansprache ist dafür in der Regel eine Einwilligung nötig.
              </span>
            </span>
          </label>

          {/*
            Kein <Field>: das ist ein <label>, und zwei Eingaben in einem Label
            wären für Screenreader eine. Jede Eingabe trägt ihren eigenen Namen.
          */}
          <div className="space-y-1.5">
            <span className="text-xs font-medium text-muted-foreground">
              Pause zwischen zwei Mails
            </span>
            <div className="flex items-center gap-2 text-sm">
              <span className="text-muted-foreground">von</span>
              <Input
                inputMode="numeric"
                aria-label="Pause von (Sekunden)"
                className="w-20"
                value={pauseMin}
                onChange={(e) => setPauseMin(e.target.value)}
              />
              <span className="text-muted-foreground">bis</span>
              <Input
                inputMode="numeric"
                aria-label="Pause bis (Sekunden)"
                className="w-20"
                value={pauseMax}
                onChange={(e) => setPauseMax(e.target.value)}
              />
              <span className="text-muted-foreground">Sekunden</span>
            </div>
            <span className="block text-xs text-muted-foreground">
              Vor jeder weiteren Mail wartet der Versand zufällig so lange — das schont das
              Postfach beim Anbieter.
              {Number(dailyLimit) > 1 &&
                Number(pauseMin) >= 0 &&
                Number(pauseMax) >= Number(pauseMin) &&
                ` Eine volle Welle von ${Number(dailyLimit)} Mails dauert damit ${versanddauer(
                  Number(dailyLimit),
                  { min: Number(pauseMin), max: Number(pauseMax) },
                )}.`}
            </span>
          </div>

          <Field label="Automatisch versenden um (optional)">
            <Input
              type="time"
              value={sendAtTime}
              onChange={(e) => setSendAtTime(e.target.value)}
            />
            <span className="text-xs text-muted-foreground">
              {sendAtTime
                ? `Täglich um ${sendAtTime} Uhr deutscher Zeit geht die nächste Welle raus — so viele, wie das Tageslimit erlaubt.`
                : 'Leer: es versendet nur „Nächste Welle freigeben“.'}
            </span>
          </Field>

          {/*
            Ohne Uhrzeit haben Startdatum und Sendetage keine Bedeutung — sie
            stünden nur da und weckten den Eindruck, es liefe etwas.
          */}
          {sendAtTime && (
            <>
              <Field label="Erster Versand ab (optional)">
                <Input
                  type="date"
                  value={sendStartDate}
                  onChange={(e) => setSendStartDate(e.target.value)}
                />
                <span className="text-xs text-muted-foreground">
                  Leer: sobald die Kampagne auf „Aktiv“ steht.
                </span>
              </Field>

              <div className="space-y-1.5">
                <span className="text-xs font-medium text-muted-foreground">Sendetage</span>
                <div className="flex flex-wrap gap-1.5">
                  {WOCHENTAGE.map((t) => {
                    const an = sendWeekdays.includes(t.ziffer);
                    return (
                      <button
                        key={t.ziffer}
                        type="button"
                        aria-pressed={an}
                        onClick={() =>
                          setSendWeekdays((alt) =>
                            an
                              ? alt.replace(t.ziffer, '')
                              : [...alt, t.ziffer].sort().join(''),
                          )
                        }
                        className={cn(
                          'rounded-md border px-2.5 py-1 text-xs font-medium transition-colors',
                          an
                            ? 'border-brand bg-brand text-white'
                            : 'border-input text-muted-foreground hover:bg-muted',
                        )}
                      >
                        {t.kurz}
                      </button>
                    );
                  })}
                </div>
              </div>
            </>
          )}
        </div>

        {error && (
          <p className="mt-4 rounded-md bg-destructive/10 p-3 text-sm text-destructive">
            {error}
          </p>
        )}

        <div className="mt-6 flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={onClose} disabled={busy}>
            Abbrechen
          </Button>
          <Button type="submit" disabled={busy}>
            {busy
              ? bearbeiten
                ? 'Speichere …'
                : 'Lege an …'
              : bearbeiten
                ? 'Speichern'
                : 'Anlegen'}
          </Button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block space-y-1.5">
      <span className="text-xs font-medium text-muted-foreground">{label}</span>
      {children}
    </label>
  );
}
